import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trophy,
  AlertCircle,
  ArrowLeft,
  Calendar,
  Users,
  Award,
  Target,
  Repeat,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { convertLocalToUTC } from "@/utils/timezone";

interface Technique {
  id: string;
  name: string;
  level: number;
}

export default function TournamentCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [techniques, setTechniques] = useState<Technique[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tournament_type: "weekly_challenge" as
      | "weekly_challenge"
      | "monthly_championship"
      | "special_event"
      | "technique_focused",
    start_time: "",
    end_time: "",
    level: 1,
    difficulty: "mixed" as "easy" | "medium" | "hard" | "mixed",
    technique_id: null as string | null,
    max_participants: 100,
    entry_fee_xp: 0,
    prize_pool_xp: 1000,
    min_rank_tier: null as string | null,
    is_recurring: false,
    recurrence_pattern: "weekly" as "daily" | "weekly" | "monthly",
    duration_hours: 168, // Default 1 week
  });

  useEffect(() => {
    fetchTechniques();
  }, []);

  const fetchTechniques = async () => {
    const { data, error } = await supabase
      .from("techniques")
      .select("id, name, level")
      .eq("is_disabled", false)
      .order("level", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching techniques:", error);
    } else {
      setTechniques(data || []);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return "Tournament name is required";

    // For non-recurring tournaments, validate dates
    if (!formData.is_recurring) {
      if (!formData.start_time) return "Start time is required";
      if (!formData.end_time) return "End time is required";

      const startDate = new Date(formData.start_time);
      const endDate = new Date(formData.end_time);
      const now = new Date();

      if (startDate < now) return "Start time must be in the future";
      if (endDate <= startDate) return "End time must be after start time";
    } else {
      // For recurring tournaments, validate duration
      if (formData.duration_hours < 1)
        return "Duration must be at least 1 hour";
      if (formData.duration_hours > 720)
        return "Duration cannot exceed 720 hours (30 days)";
    }

    if (formData.level < 1 || formData.level > 10)
      return "Level must be between 1 and 10";
    if (formData.max_participants < 2)
      return "Must allow at least 2 participants";
    if (formData.prize_pool_xp < 0) return "Prize pool cannot be negative";
    if (formData.entry_fee_xp < 0) return "Entry fee cannot be negative";

    if (
      formData.tournament_type === "technique_focused" &&
      !formData.technique_id
    ) {
      return "Technique-focused tournaments must have a technique selected";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        description: formData.description,
        tournament_type: formData.tournament_type,
        level: formData.level,
        difficulty: formData.difficulty,
        technique_id: formData.technique_id,
        max_participants: formData.max_participants,
        entry_fee_xp: formData.entry_fee_xp,
        prize_pool_xp: formData.prize_pool_xp,
        min_rank_tier: formData.min_rank_tier,
        is_recurring: formData.is_recurring,
      };

      if (formData.is_recurring) {
        payload.recurrence_pattern = formData.recurrence_pattern;
        payload.duration_hours = formData.duration_hours;
      } else {
        // Convert local datetime to UTC for database storage
        payload.start_time = convertLocalToUTC(formData.start_time);
        payload.end_time = convertLocalToUTC(formData.end_time);
      }

      const result = await api.createTournament(payload);

      if (!result.success) {
        setError("Failed to create tournament");
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);

      // Navigate to competitive page after 2 seconds
      setTimeout(() => {
        navigate("/competitive");
      }, 2000);
    } catch (err: any) {
      console.error("Unexpected error:", err);
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => navigate("/competitive")}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tournaments
      </Button>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-yellow-600" />
          <h1 className="text-3xl font-bold">Create Tournament</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Set up a new tournament for the community to compete in
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">
            Tournament created successfully! Redirecting to tournaments page...
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Tournament Details</CardTitle>
          <CardDescription>
            Fill in the information below to create your tournament
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Basic Information
              </h3>

              <div>
                <Label htmlFor="name">Tournament Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Weekly Speed Challenge"
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Describe what makes this tournament special..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div>
                <Label htmlFor="tournament_type">Tournament Type *</Label>
                <Select
                  value={formData.tournament_type}
                  onValueChange={(value) =>
                    handleInputChange("tournament_type", value)
                  }
                >
                  <SelectTrigger id="tournament_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly_challenge">
                      Weekly Challenge
                    </SelectItem>
                    <SelectItem value="monthly_championship">
                      Monthly Championship
                    </SelectItem>
                    <SelectItem value="special_event">Special Event</SelectItem>
                    <SelectItem value="technique_focused">
                      Technique Focused
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recurring Tournament Toggle */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                <Checkbox
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) =>
                    handleInputChange("is_recurring", checked)
                  }
                />
                <div className="flex-grow">
                  <Label
                    htmlFor="is_recurring"
                    className="font-medium cursor-pointer"
                  >
                    <Repeat className="w-4 h-4 inline mr-2" />
                    Make this a recurring tournament
                  </Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Automatically create new tournaments on a schedule (daily,
                    weekly, or monthly)
                  </p>
                </div>
              </div>
            </div>

            {/* Schedule (for non-recurring) or Recurrence (for recurring) */}
            {!formData.is_recurring ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Schedule
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="datetime-local"
                      value={formData.start_time}
                      onChange={(e) =>
                        handleInputChange("start_time", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="datetime-local"
                      value={formData.end_time}
                      onChange={(e) =>
                        handleInputChange("end_time", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Repeat className="w-5 h-5" />
                  Recurrence Settings
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="recurrence_pattern">
                      Recurrence Pattern *
                    </Label>
                    <Select
                      value={formData.recurrence_pattern}
                      onValueChange={(value) =>
                        handleInputChange("recurrence_pattern", value)
                      }
                    >
                      <SelectTrigger id="recurrence_pattern">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">
                          Daily - New tournament every day
                        </SelectItem>
                        <SelectItem value="weekly">
                          Weekly - New tournament every week
                        </SelectItem>
                        <SelectItem value="monthly">
                          Monthly - New tournament every month
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="duration_hours">Duration (Hours) *</Label>
                    <Input
                      id="duration_hours"
                      type="number"
                      min="1"
                      max="720"
                      value={formData.duration_hours}
                      onChange={(e) =>
                        handleInputChange(
                          "duration_hours",
                          parseInt(e.target.value),
                        )
                      }
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long each tournament instance will run (1-720 hours)
                    </p>
                  </div>
                </div>

                <Alert>
                  <AlertDescription className="text-sm">
                    <strong>Note:</strong> The first tournament will be
                    automatically created by the system shortly after you save
                    this template. Future tournaments will be generated based on
                    the recurrence pattern.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Difficulty Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Target className="w-5 h-5" />
                Difficulty Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="level">Tournament Level (1-10) *</Label>
                  <Input
                    id="level"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.level}
                    onChange={(e) =>
                      handleInputChange("level", parseInt(e.target.value))
                    }
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Level {formData.level} includes techniques from levels 1-
                    {formData.level}
                  </p>
                </div>

                <div>
                  <Label htmlFor="difficulty">Difficulty *</Label>
                  <Select
                    value={formData.difficulty}
                    onValueChange={(value) =>
                      handleInputChange("difficulty", value)
                    }
                  >
                    <SelectTrigger id="difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Technique Selector (only for technique_focused) */}
              {formData.tournament_type === "technique_focused" && (
                <div>
                  <Label htmlFor="technique_id">Technique *</Label>
                  <Select
                    value={formData.technique_id || ""}
                    onValueChange={(value) =>
                      handleInputChange("technique_id", value || null)
                    }
                  >
                    <SelectTrigger id="technique_id">
                      <SelectValue placeholder="Select a technique" />
                    </SelectTrigger>
                    <SelectContent>
                      {techniques.map((tech) => (
                        <SelectItem key={tech.id} value={tech.id}>
                          {tech.name} (Level {tech.level})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Participation Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Participation
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_participants">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    min="2"
                    max="10000"
                    value={formData.max_participants}
                    onChange={(e) =>
                      handleInputChange(
                        "max_participants",
                        parseInt(e.target.value),
                      )
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="min_rank_tier">Minimum Rank (Optional)</Label>
                  <Select
                    value={formData.min_rank_tier || "none"}
                    onValueChange={(value) =>
                      handleInputChange(
                        "min_rank_tier",
                        value === "none" ? null : value,
                      )
                    }
                  >
                    <SelectTrigger id="min_rank_tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Requirement</SelectItem>
                      <SelectItem value="bronze">Bronze</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="platinum">Platinum</SelectItem>
                      <SelectItem value="diamond">Diamond</SelectItem>
                      <SelectItem value="master">Master</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Rewards */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Award className="w-5 h-5" />
                Rewards & Entry
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prize_pool_xp">Prize Pool (XP) *</Label>
                  <Input
                    id="prize_pool_xp"
                    type="number"
                    min="0"
                    step="100"
                    value={formData.prize_pool_xp}
                    onChange={(e) =>
                      handleInputChange(
                        "prize_pool_xp",
                        parseInt(e.target.value),
                      )
                    }
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    1st: {Math.floor(formData.prize_pool_xp * 0.5)} XP, 2nd:{" "}
                    {Math.floor(formData.prize_pool_xp * 0.3)} XP, 3rd:{" "}
                    {Math.floor(formData.prize_pool_xp * 0.2)} XP
                  </p>
                </div>

                <div>
                  <Label htmlFor="entry_fee_xp">Entry Fee (XP)</Label>
                  <Input
                    id="entry_fee_xp"
                    type="number"
                    min="0"
                    step="10"
                    value={formData.entry_fee_xp}
                    onChange={(e) =>
                      handleInputChange(
                        "entry_fee_xp",
                        parseInt(e.target.value),
                      )
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    0 = Free to enter
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/competitive")}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? "Creating..." : "Create Tournament"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
